import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiFetch } from './utils/api';
import PageHeader from './components/PageHeader';
import scheduleSnap from './utils/scheduleSnap';
import scheduleNav from './schedule-nav';
import dispatchDrag from './dispatch-drag';
import unallocatedJobs from './unallocated-jobs';
import jobStatuses from './constants/job-statuses';
import dispatchRefresh from './dispatch-refresh';

const dateKey = scheduleNav.formatDateKey;
// The day tile already draws five lines at 11, 11, 10, 9 and 9px, with p-1
// and a 1px border. 90px is that content. 4px is the existing top-1 inset.
const DAY_TILE_HEIGHT = 90;
const DAY_TILE_INSET = 4;

function EngineerDispatch() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // The address is the source of truth. A second copy in state was writing the
  // old day back over a date the user had just picked.
  const query = scheduleNav.parseScheduleQuery(searchParams);
  const currentDate = query.date;
  const view = query.view;
  const dragRef = useRef(null);
  const [hoursView, setHoursView] = useState('working'); // 'working', 'full'
  const [people, setPeople] = useState([]);
  const [unassignedJobs, setUnassignedJobs] = useState([]);
  const [scheduleItems, setScheduleItems] = useState([]);
  const [filterPerson, setFilterPerson] = useState('engineers');
  const [draggedJob, setDraggedJob] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [unallocatedQuery, setUnallocatedQuery] = useState('');
  const [unallocatedSort, setUnallocatedSort] = useState('created');
  const [workingHoursStart, setWorkingHoursStart] = useState(7);
  const [workingHoursEnd, setWorkingHoursEnd] = useState(19);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [jobDetail, setJobDetail] = useState(null);
  const [editingJobId, setEditingJobId] = useState(null);
  const [editingJob, setEditingJob] = useState(null);
  const [editStatusOrigin, setEditStatusOrigin] = useState('');
  const [editStatusReason, setEditStatusReason] = useState('');
  const [statusColors, setStatusColors] = useState({});
  const [slotMinutes, setSlotMinutes] = useState(() => (
    scheduleSnap.normaliseSlotMinutes(window.localStorage.getItem('doorbase_dispatch_slot_minutes'))
  ));
  const [dropPreview, setDropPreview] = useState(null);

  // Generate time slots or dates based on view
  const hours = hoursView === 'working' 
    ? Array.from({ length: workingHoursEnd - workingHoursStart }, (_, i) => i + workingHoursStart)
    : Array.from({ length: 24 }, (_, i) => i);

  // Generate week dates (Mon-Sun)
  const getWeekDates = () => {
    const dates = [];
    const start = new Date(currentDate);
    start.setDate(start.getDate() - ((start.getDay() + 6) % 7));
    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  // Generate month dates
  const getMonthDates = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const dates = [];
    
    // Add days from previous month to start on Monday
    const startDay = firstDay.getDay() || 7; // Convert Sunday (0) to 7
    for (let i = startDay - 1; i > 0; i--) {
      const date = new Date(year, month, 1 - i);
      dates.push(date);
    }
    
    // Add all days of current month
    for (let i = 1; i <= lastDay.getDate(); i++) {
      dates.push(new Date(year, month, i));
    }
    
    // Add days from next month to complete the grid
    const remainingDays = 35 - dates.length;
    for (let i = 1; i <= remainingDays; i++) {
      dates.push(new Date(year, month + 1, i));
    }
    
    return dates;
  };

  const weekDates = view === 'week' ? getWeekDates() : [];
  const monthDates = view === 'month' ? getMonthDates() : [];

  const dateParam = dateKey(currentDate);

  // Reference data is loaded once. Repeating it on every render was refetching
  // the same six endpoints several times a second and collapsing the job list
  // under the pointer, so a drop never reached a save.
  useEffect(() => {
    const gate = { cancelled: false };
    Promise.all([
      fetchCompanySettings(gate),
      fetchPeople(gate),
    ]);
    return () => { gate.cancelled = true; };
  }, []);

  useEffect(() => {
    const gate = { cancelled: false };
    const loadBoard = () => {
      fetchUnassignedJobs(gate);
      fetchSchedule(gate);
      fetchStatusColors(gate);
    };
    loadBoard();
    const timer = setInterval(loadBoard, dispatchRefresh.STATUS_POLL_MS);
    return () => {
      gate.cancelled = true;
      clearInterval(timer);
    };
  }, [dateParam, view]);

  const replaceQuery = (date, nextView) => {
    const next = scheduleNav.formatScheduleQuery(date, nextView);
    if (searchParams.get('date') === next.get('date') && searchParams.get('view') === next.get('view')) return;
    const merged = new URLSearchParams(searchParams);
    merged.set('date', next.get('date'));
    merged.set('view', next.get('view'));
    setSearchParams(merged, { replace: true });
  };

  // A visit with no date in the address is written once, so a refresh stays put.
  useEffect(() => {
    const canonical = scheduleNav.formatScheduleQuery(currentDate, view);
    if (searchParams.get('date') === canonical.get('date') && searchParams.get('view') === canonical.get('view')) return;
    const merged = new URLSearchParams(searchParams);
    merged.set('date', canonical.get('date'));
    merged.set('view', canonical.get('view'));
    setSearchParams(merged, { replace: true });
  }, [dateParam, view, searchParams, setSearchParams, currentDate]);

  const stillMounted = (gate) => !gate || !gate.cancelled;

  const fetchCompanySettings = async (gate) => {
    try {
      const res = await apiFetch('/api/company-settings');
      const data = await res.json();
      if (!stillMounted(gate)) return;
      if (data.working_hours_start) setWorkingHoursStart(Number(data.working_hours_start));
      if (data.working_hours_end) setWorkingHoursEnd(Number(data.working_hours_end));
    } catch (err) {
      console.error('Error fetching company settings:', err);
    }
  };

  const fetchStatusColors = async (gate) => {
    try {
      const res = await apiFetch('/api/job-status-colors');
      const data = await res.json();
      if (!stillMounted(gate)) return;
      setStatusColors(jobStatuses.colourLookup(data));
    } catch (err) {
      console.error('Error fetching status colors:', err);
    }
  };

  const fetchPeople = async (gate) => {
    try {
      const res = await apiFetch('/api/employees');
      const data = await res.json();
      if (!stillMounted(gate)) return;
      setPeople(data || []);
    } catch (err) {
      console.error('Error fetching people:', err);
    }
  };

  const fetchUnassignedJobs = async (gate) => {
    try {
      const res = await apiFetch('/api/jobs/unassigned');
      const data = await res.json();
      if (!stillMounted(gate)) return;
      setUnassignedJobs(data || []);
    } catch (err) {
      console.error('Error fetching unassigned jobs:', err);
    }
  };

  const fetchSchedule = async (gate) => {
    try {
      const dates = view === 'week' ? getWeekDates() : view === 'month' ? getMonthDates() : [currentDate];
      const res = await apiFetch(`/api/schedule?start_date=${dateKey(dates[0])}&end_date=${dateKey(dates[dates.length-1])}`);
      const data = await res.json();
      if (!stillMounted(gate)) return;
      setScheduleItems((data || []).map(item => ({
        ...item,
        person_id: Number(item.person_id),
        start_hour: Number(item.start_hour),
        start_minute: Number(item.start_minute || 0),
        duration_hours: Number(item.duration_hours)
      })));
    } catch (err) {
      console.error('Error fetching schedule:', err);
    }
  };

  const dayStart = hours[0];
  const dayEnd = hours[hours.length - 1] + 1;

  const beginDrag = (event, job) => {
    const payload = dispatchDrag.payloadFromJob(job);
    dragRef.current = payload;
    dispatchDrag.writePayload(event, payload);
    const board = event.currentTarget && event.currentTarget.closest
      ? event.currentTarget.closest('[data-dispatch-board]')
      : null;
    if (board) board.setAttribute('data-dragging', '1');
  };

  const endDrag = (event) => {
    const board = event.currentTarget && event.currentTarget.closest
      ? event.currentTarget.closest('[data-dispatch-board]')
      : null;
    if (board) board.removeAttribute('data-dragging');
    const payload = dragRef.current;
    // drop runs before dragend. Clear on the next turn so the drop can still read the ref.
    setTimeout(() => {
      if (dragRef.current === payload) dragRef.current = null;
      setDraggedJob(null);
      setDropPreview(null);
    }, 0);
  };

  const snappedFromLane = (event, lane) => {
    const rect = lane.getBoundingClientRect();
    return scheduleSnap.snapDrop({
      clientX: event.clientX,
      laneLeft: rect.left,
      laneWidth: rect.width,
      dayStartHour: dayStart,
      dayEndHour: dayEnd,
      slotMinutes
    });
  };

  const handleJobClick = async (e, jobId) => {
    // Don't open modal if dragging
    if (e.defaultPrevented) return;
    e.stopPropagation();
    
    // Fetch job details
    try {
      const res = await apiFetch(`/api/jobs/${jobId}`);
      const data = await res.json();
      setJobDetail(data);
      setSelectedJobId(jobId);
    } catch (err) {
      console.error('Error fetching job details:', err);
    }
  };

  const closeJobModal = () => {
    setSelectedJobId(null);
    setJobDetail(null);
  };

  const saveDrop = async (payload, personId, date, snapped) => {
    const duration = scheduleSnap.blockDurationHours(payload);
    const previousItems = scheduleItems;
    const previousUnassigned = unassignedJobs;
    const dateText = dateKey(date);
    const optimisticId = payload.schedule_id || `temp-${payload.job_id}`;
    const optimistic = {
      id: optimisticId,
      job_id: payload.job_id,
      job_number: payload.job_number,
      person_id: Number(personId),
      schedule_date: dateText,
      start_hour: snapped.start_hour,
      start_minute: snapped.start_minute,
      duration_hours: duration,
      customer_name: payload.customer_name,
      site_name: payload.site_name,
      job_status: payload.job_status && payload.job_status !== 'New' ? payload.job_status : 'Scheduled'
    };

    setScheduleItems((items) => {
      const without = items.filter((item) => (
        String(item.id) !== String(payload.schedule_id)
        && String(item.job_id) !== String(payload.job_id)
      ));
      return [...without, optimistic];
    });
    if (!payload.schedule_id) {
      setUnassignedJobs((jobs) => jobs.filter((job) => String(job.id) !== String(payload.job_id)));
    }
    setDropPreview(null);
    setDraggedJob(null);
    dragRef.current = null;

    const body = {
      person_id: Number(personId),
      date: dateText,
      schedule_date: dateText,
      start_hour: snapped.start_hour,
      start_minute: snapped.start_minute,
      duration_hours: duration,
      slot_minutes: slotMinutes
    };
    const moving = Boolean(payload.schedule_id);
    try {
      const res = await apiFetch(moving ? `/api/schedule/${payload.schedule_id}` : '/api/schedule', {
        method: moving ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(moving ? body : { ...body, job_id: payload.job_id })
      });
      if (!res.ok) {
        setScheduleItems(previousItems);
        setUnassignedJobs(previousUnassigned);
        alert('Failed to schedule job');
        return;
      }
      const saved = await res.json();
      setScheduleItems((items) => items.map((item) => (
        String(item.job_id) === String(payload.job_id)
          ? {
            ...item,
            ...saved,
            id: saved.id,
            job_status: saved.job_status || item.job_status,
            customer_name: saved.customer_name || item.customer_name,
            site_name: saved.site_name || item.site_name,
            job_number: saved.job_number || item.job_number,
            person_id: Number(saved.person_id),
            start_hour: Number(saved.start_hour),
            start_minute: Number(saved.start_minute || 0),
            duration_hours: Number(saved.duration_hours)
          }
          : item
      )));
    } catch (err) {
      console.error('Error scheduling job:', err);
      setScheduleItems(previousItems);
      setUnassignedJobs(previousUnassigned);
      alert('Failed to schedule job');
    }
  };

  const handleDrop = async (e, personId, lane) => {
    e.preventDefault();
    const payload = dispatchDrag.readPayload(e, dragRef);
    if (!payload) return;

    const startMinutes = snappedFromLane(e, lane);
    const snapped = scheduleSnap.snapToSlot({
      startHour: Math.floor(startMinutes / 60),
      startMinute: startMinutes % 60,
      slotMinutes,
      rangeStart: dayStart,
      rangeEnd: dayEnd
    });
    await saveDrop(payload, personId, currentDate, snapped);
  };

  const handleDropOnDate = async (e, personId, date) => {
    e.preventDefault();
    const payload = dispatchDrag.readPayload(e, dragRef);
    if (!payload) return;
    const snapped = scheduleSnap.snapToSlot({
      startHour: workingHoursStart,
      startMinute: 0,
      slotMinutes: 15,
      rangeStart: 0,
      rangeEnd: 24
    });
    await saveDrop(payload, personId, date, snapped);
  };

  const handleDragOver = (e, personId, lane) => {
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
    const payload = dragRef.current;
    if (!payload || !lane || !lane.getBoundingClientRect) return;
    const startMinutes = snappedFromLane(e, lane);
    setDropPreview((prev) => {
      if (prev && Number(prev.personId) === Number(personId) && prev.startMinutes === startMinutes) return prev;
      return {
        personId: Number(personId),
        startMinutes,
        durationHours: scheduleSnap.blockDurationHours(payload)
      };
    });
    if (!draggedJob) setDraggedJob(payload);
  };

  const navigateDate = (direction) => {
    replaceQuery(scheduleNav.stepDate(currentDate, view, direction), view);
  };

  const goToToday = () => {
    replaceQuery(scheduleNav.startOfDay(new Date()), view);
  };

  const jumpToDate = (value) => {
    const parsed = scheduleNav.parseDateKey(value);
    if (parsed) replaceQuery(parsed, view);
  };

  const formatHour = (hour) => {
    if (hour === 0) return '12am';
    if (hour < 12) return `${hour}am`;
    if (hour === 12) return '12pm';
    return `${hour - 12}pm`;
  };

  const formatClock = (hour, minute) => {
    const mins = Number(minute || 0);
    if (!mins) return formatHour(Number(hour));
    const h = Number(hour);
    const suffix = h < 12 ? 'am' : 'pm';
    const hour12 = h % 12 === 0 ? 12 : h % 12;
    return `${hour12}:${String(mins).padStart(2, '0')}${suffix}`;
  };

  const changeSlotMinutes = (value) => {
    const next = scheduleSnap.normaliseSlotMinutes(value);
    setSlotMinutes(next);
    window.localStorage.setItem('doorbase_dispatch_slot_minutes', String(next));
  };

  const filteredPeople = filterPerson === 'all' 
    ? people 
    : filterPerson === 'engineers'
    ? people.filter(p => {
        const role = (p.role || p.job_title || '').toLowerCase();
        return role.includes('engineer') || role.includes('technician');
      })
    : filterPerson === 'office'
    ? people.filter(p => {
        const role = (p.role || p.job_title || '').toLowerCase();
        return !role.includes('engineer') && !role.includes('technician');
      })
    : people.filter(p => p.id === parseInt(filterPerson));

  const visibleJobs = unallocatedJobs.visibleUnallocated(unassignedJobs, unallocatedQuery, unallocatedSort);

  const returnToUnallocated = async (event) => {
    event.preventDefault();
    const payload = dispatchDrag.readPayload(event, dragRef);
    if (!payload || !payload.schedule_id) return;
    const label = payload.label || 'this job';
    if (!window.confirm(`Return ${label} to unallocated? The engineer and date will be cleared.`)) return;
    const previousItems = scheduleItems;
    const previousUnassigned = unassignedJobs;
    setScheduleItems((items) => items.filter((item) => (
      String(item.id) !== String(payload.schedule_id) && String(item.job_id) !== String(payload.job_id)
    )));
    setUnassignedJobs((jobs) => (
      jobs.some((job) => String(job.id) === String(payload.job_id))
        ? jobs
        : [{
          id: payload.job_id,
          job_number: payload.job_number,
          company_name: payload.customer_name,
          site_name: payload.site_name,
          job_category: payload.job_category,
          estimated_hours: payload.duration_hours,
          job_status: 'New',
        }, ...jobs]
    ));
    setDropPreview(null);
    setDraggedJob(null);
    dragRef.current = null;
    try {
      const res = await apiFetch(`/api/schedule/${payload.schedule_id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('delete failed');
      fetchUnassignedJobs();
    } catch (err) {
      console.error('Error returning job to unallocated:', err);
      setScheduleItems(previousItems);
      setUnassignedJobs(previousUnassigned);
      alert('Failed to unallocate job');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md sticky top-0 z-10" style={{ height: '116px' }}>
        <div className="max-w-full mx-auto px-6 h-full flex flex-col justify-center">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/ops/daily-planning')}
              className="text-white hover:text-blue-100 transition text-lg font-semibold"
            >
              ⬅ Back
            </button>
            <h1 className="text-4xl font-bold">Engineer Daily Dispatch</h1>
            
            {/* Date Navigation */}
            <div className="flex items-center gap-2">
              <button
                type="button"
                aria-label="Previous period"
                data-testid="dispatch-prev"
                onClick={() => navigateDate(-1)}
                className="px-3 py-2 bg-white text-black hover:bg-gray-100 rounded transition"
              >
                ◀
              </button>
              <button
                type="button"
                data-testid="dispatch-today"
                onClick={goToToday}
                className="px-4 py-2 bg-white text-black hover:bg-gray-100 rounded font-medium transition"
              >
                Today
              </button>
              <input
                type="date"
                data-testid="dispatch-date"
                aria-label="Jump to date"
                value={dateParam}
                onChange={(e) => jumpToDate(e.target.value)}
                className="px-2 py-2 bg-white text-black rounded"
              />
              <button
                type="button"
                aria-label="Next period"
                data-testid="dispatch-next"
                onClick={() => navigateDate(1)}
                className="px-3 py-2 bg-white text-black hover:bg-gray-100 rounded transition"
              >
                ▶
              </button>
            </div>

            {/* View Toggle & Hours Toggle */}
            <div className="flex gap-2">
              <div className="flex gap-1 bg-gray-200 rounded-lg p-1">
                {['day', 'week', 'month'].map((v) => (
                  <button
                    key={v}
                    type="button"
                    data-testid={`dispatch-view-${v}`}
                    onClick={() => replaceQuery(currentDate, v)}
                    className={`px-4 py-2 rounded-lg transition text-sm font-medium ${
                      view === v 
                        ? 'bg-gray-400 text-black shadow-sm' 
                        : 'bg-white text-black hover:bg-gray-100'
                    }`}
                  >
                    {v.charAt(0).toUpperCase() + v.slice(1)}
                  </button>
                ))}
              </div>
              
              <div className="flex gap-1 bg-gray-200 rounded-lg p-1">
                <button
                  onClick={() => setHoursView('working')}
                  className={`px-4 py-2 rounded-lg transition text-sm font-medium ${
                    hoursView === 'working' 
                      ? 'bg-gray-400 text-black shadow-sm' 
                      : 'bg-white text-black hover:bg-gray-100'
                  }`}
                >
                  Working Hours
                </button>
                <button
                  onClick={() => setHoursView('full')}
                  className={`px-4 py-2 rounded-lg transition text-sm font-medium ${
                    hoursView === 'full' 
                      ? 'bg-gray-400 text-black shadow-sm' 
                      : 'bg-white text-black hover:bg-gray-100'
                  }`}
                >
                  24 Hours
                </button>
              </div>

              <label className="flex items-center gap-2 bg-white text-black rounded-lg px-3 text-sm font-medium">
                Slot
                <select
                  data-testid="dispatch-slot-minutes"
                  aria-label="Slot size"
                  value={slotMinutes}
                  onChange={(e) => changeSlotMinutes(e.target.value)}
                  className="bg-white text-black"
                >
                  <option value={15}>15 min</option>
                  <option value={30}>30 min</option>
                  <option value={60}>60 min</option>
                </select>
              </label>
            </div>
          </div>


        </div>
      </div>

      <div
        data-testid="dispatch-status-legend"
        className="flex flex-wrap items-center gap-2 px-4 py-2 bg-white border-b border-gray-200"
        aria-label="Job status colours"
      >
        {jobStatuses.JOB_STATUSES.map((status) => {
          const item = jobStatuses.appearance(status.value, statusColors);
          return (
            <span
              key={status.value}
              data-status={item.status}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold ${item.badgeClass}`}
            >
              <span aria-hidden="true">{item.icon}</span>
              {item.label}
            </span>
          );
        })}
      </div>

      <div data-dispatch-board className="flex" style={{ height: 'calc(100vh - 216px)', maxHeight: 'calc(100vh - 216px)' }}>
        {!sidebarCollapsed && (
          <div
            className="w-56 flex flex-col"
            data-testid="dispatch-unallocated-drop"
            onDragOver={(event) => event.preventDefault()}
            onDrop={returnToUnallocated}
          >
          <div className="px-4 bg-gray-400 flex items-center justify-between flex-shrink-0" style={{ height: '44px' }}>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSidebarCollapsed(true)}
                className="text-black hover:text-gray-700 text-sm"
              >
                ◀
              </button>
              <h2 className="font-bold text-black text-sm">Unallocated Jobs</h2>
            </div>
            <span className="bg-gray-600 text-white text-xs font-semibold px-2 py-1 rounded-full min-w-[24px] text-center">
              {visibleJobs.length}
            </span>
          </div>
          <div className="bg-blue-700 border-r border-gray-200 px-2 py-2 space-y-2 flex-shrink-0">
            <input
              type="search"
              data-testid="dispatch-unallocated-search"
              aria-label="Search unallocated jobs"
              value={unallocatedQuery}
              onChange={(event) => setUnallocatedQuery(event.target.value)}
              placeholder="Search jobs"
              className="w-full px-2 py-1 rounded text-sm text-black"
            />
            <select
              data-testid="dispatch-unallocated-sort"
              aria-label="Sort unallocated jobs"
              value={unallocatedSort}
              onChange={(event) => setUnallocatedSort(event.target.value)}
              className="w-full px-2 py-1 rounded text-sm text-black"
            >
              <option value="created">Newest created</option>
              <option value="created-asc">Oldest created</option>
              <option value="type">Job type</option>
            </select>
          </div>
          <div className="flex-1 overflow-y-auto bg-blue-600 border-r border-gray-200">
            <div className="mt-2 space-y-2 px-2 pb-2">
              {visibleJobs.map((job) => {
                const appearance = jobStatuses.appearance(job.job_status, statusColors);
                return (
                <div
                  key={job.id}
                  draggable
                  data-testid={`unassigned-job-${job.id}`}
                  data-status={appearance.status}
                  onDragStart={(e) => beginDrag(e, job)}
                  onDragEnd={endDrag}
                  onClick={(e) => handleJobClick(e, job.id)}
                  className={`p-2 border rounded cursor-pointer hover:shadow-md transition ${appearance.blockClass}`}
                  title="Click to view job details, drag to schedule"
                >
                  <div className="font-semibold text-xs mb-1">
                    {job.job_number}
                  </div>
                  <div className="text-[10px] font-semibold mb-1">{appearance.icon} {appearance.label}</div>
                  <div className="text-[11px] text-gray-600 space-y-0.5">
                    <div className="truncate">{job.company_name || 'Customer'}</div>
                    <div className="text-[10px] text-gray-500 truncate">{job.site_name || 'Site'}</div>
                    <div className="flex justify-between items-center mt-1">
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium ${
                        job.call_priority === 'Emergency' ? 'bg-red-100 text-red-800' :
                        job.call_priority === 'Urgent' ? 'bg-orange-100 text-orange-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {job.job_category || job.call_priority || 'Job'}
                      </span>
                      <span className="text-[10px]">⏱️ {job.estimated_hours || 1}h</span>
                    </div>
                  </div>
                </div>
                );
              })}
              {visibleJobs.length === 0 && (
                <div className="text-center py-4 text-white text-sm italic">
                  No unallocated jobs
                </div>
              )}
            </div>
          </div>
          {/* DoorBase Logo - pushed to bottom */}
          <div className="bg-blue-600 border-r border-gray-200 p-4 flex items-center justify-center mt-auto flex-shrink-0">
            <div className="text-white text-center">
              <img src="/favicon.svg" alt="DoorBase" className="w-16 h-16 mx-auto mb-2" />
              <div className="text-2xl font-bold">DoorBase</div>
              <div className="text-xs opacity-75">Dispatch Management</div>
            </div>
          </div>
        </div>
        )}

        {/* Main Schedule Area */}
        <div className="flex-1 overflow-auto pb-4">
          {view === 'day' && (
            <div className="pb-4">
              {/* Time Header */}
              <div className="flex sticky top-0 border-b border-gray-500 z-10 bg-gray-400" style={{ height: '44px' }}>
                {sidebarCollapsed && (
                  <div className="w-10 flex-shrink-0 px-1 bg-gray-400 border-r border-gray-500 flex items-center justify-center">
                    <button
                      onClick={() => setSidebarCollapsed(false)}
                      className="text-black hover:text-gray-700 text-sm"
                    >
                      ▶
                    </button>
                  </div>
                )}
                <div className="flex-shrink-0 px-2 text-black bg-gray-400 border-r border-gray-500 flex items-center justify-center" style={{ width: '200px' }}>
                  <select
                    value={filterPerson}
                    onChange={(e) => setFilterPerson(e.target.value)}
                    className="w-full px-2 py-1 bg-gray-300 border border-gray-400 rounded text-black text-sm font-semibold text-center focus:outline-none focus:ring-1 focus:ring-gray-500"
                  >
                    <option value="all" className="bg-gray-300 text-black">All Users</option>
                    <option value="engineers" className="bg-gray-300 text-black font-semibold">All Engineers</option>
                    <option value="office" className="bg-gray-300 text-black font-semibold">All Office Staff</option>
                    <option disabled className="bg-gray-400 text-black">────────────</option>
                    <optgroup label="Engineers" className="bg-gray-300 text-black">
                      {people.filter(p => {
                        const role = (p.role || p.job_title || '').toLowerCase();
                        return role.includes('engineer') || role.includes('technician');
                      }).map(p => (
                        <option key={p.id} value={p.id} className="bg-gray-300 text-black">
                          {p.first_name} {p.last_name}
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="Office Staff" className="bg-gray-300 text-black">
                      {people.filter(p => {
                        const role = (p.role || p.job_title || '').toLowerCase();
                        return !role.includes('engineer') && !role.includes('technician');
                      }).map(p => (
                        <option key={p.id} value={p.id} className="bg-gray-300 text-black">
                          {p.first_name} {p.last_name}
                        </option>
                      ))}
                    </optgroup>
                  </select>
                </div>
                {hours.map(hour => (
                  <div
                    key={hour}
                    className="flex-1 px-2 text-center text-xs font-bold border-r border-gray-500 bg-gray-400 text-black flex items-center justify-center"
                  >
                    {formatHour(hour)}
                  </div>
                ))}
              </div>

            {/* Person Rows */}
            {filteredPeople.map((person, index) => {
              const dayItems = scheduleItems.filter((item) => Number(item.person_id) === Number(person.id));
              const lanes = scheduleSnap.packDayLanes(dayItems.map((item) => ({
                key: item.id,
                startMinutes: scheduleSnap.itemStartMinutes(item),
                durationHours: scheduleSnap.blockDurationHours(item),
              })));
              const rowLanes = dayItems.reduce((max, item) => Math.max(max, (lanes[item.id] || {}).laneCount || 1), 0);
              const previewHere = dropPreview && Number(dropPreview.personId) === Number(person.id);
              const lanesForHeight = Math.max(rowLanes, previewHere ? 1 : 0);
              const rowMinHeight = lanesForHeight > 0
                ? DAY_TILE_INSET + lanesForHeight * (DAY_TILE_HEIGHT + DAY_TILE_INSET)
                : undefined;
              return (
              <div key={person.id} data-testid={`dispatch-row-${person.id}`} data-person-id={person.id} className={`flex border-b border-gray-200 hover:bg-gray-50`}>
                {sidebarCollapsed && (
                  <div className={`w-10 flex-shrink-0 border-r border-gray-300 ${index % 2 === 1 ? 'bg-yellow-50' : 'bg-white'}`}></div>
                )}
                <div className={`flex-shrink-0 p-3 font-medium text-gray-900 border-r border-gray-300 flex items-center justify-center ${index % 2 === 1 ? 'bg-yellow-50' : 'bg-white'}`} style={{ width: '200px' }}>
                  <div className="text-center">
                    <div className="text-sm">{person.first_name} {person.last_name}</div>
                    <div className="text-xs text-gray-500">{person.role || 'Engineer'}</div>
                  </div>
                </div>

                <div
                  data-testid={`dispatch-lane-${person.id}`}
                  data-person-id={person.id}
                  onDragOver={(e) => handleDragOver(e, person.id, e.currentTarget)}
                  onDrop={(e) => handleDrop(e, person.id, e.currentTarget)}
                  className={`flex-1 min-h-[70px] relative overflow-hidden ${index % 2 === 1 ? 'bg-yellow-50' : 'bg-white'}`}
                  style={rowMinHeight ? { minHeight: `${rowMinHeight}px` } : undefined}
                >
                  <div className="absolute inset-0 flex pointer-events-none">
                    {hours.map((hour) => (
                      <div key={hour} className="flex-1 border-r border-gray-200" />
                    ))}
                  </div>
                  {dropPreview && Number(dropPreview.personId) === Number(person.id) && (
                    <div
                      data-testid="dispatch-drop-preview"
                      data-person-id={person.id}
                      data-start-minutes={dropPreview.startMinutes}
                      data-duration-hours={dropPreview.durationHours}
                      className="absolute rounded border-2 border-dashed border-blue-700 bg-blue-200/70 pointer-events-none z-20"
                      style={{
                        ...scheduleSnap.placementStyle(dropPreview.startMinutes, dropPreview.durationHours, dayStart, dayEnd),
                        top: `${DAY_TILE_INSET}px`,
                        height: `${DAY_TILE_HEIGHT}px`,
                      }}
                    />
                  )}
                  {dayItems.map((scheduledItem) => {
                    const statusConfig = jobStatuses.appearance(scheduledItem.job_status, statusColors);
                    const startMinutes = scheduleSnap.itemStartMinutes(scheduledItem);
                    const duration = scheduleSnap.blockDurationHours(scheduledItem);
                    const lane = lanes[scheduledItem.id] || { lane: 0, laneCount: 1 };
                    return (
                      <div
                        key={scheduledItem.id}
                        data-testid={`schedule-block-${scheduledItem.job_id}`}
                        data-schedule-block
                        data-schedule-id={scheduledItem.id}
                        data-status={statusConfig.status}
                        data-person-id={person.id}
                        data-start-minutes={startMinutes}
                        data-duration-hours={duration}
                        data-lane={lane.lane}
                        draggable
                        onDragStart={(e) => beginDrag(e, { ...scheduledItem, schedule_id: scheduledItem.id })}
                        onDragEnd={endDrag}
                        onClick={(e) => handleJobClick(e, scheduledItem.job_id)}
                        className={`absolute rounded border p-1 text-xs overflow-hidden cursor-pointer transition z-10 ${statusConfig.blockClass}`}
                        style={{
                          ...scheduleSnap.placementStyle(startMinutes, duration, dayStart, dayEnd),
                          top: `${DAY_TILE_INSET + lane.lane * (DAY_TILE_HEIGHT + DAY_TILE_INSET)}px`,
                          height: `${DAY_TILE_HEIGHT}px`,
                          pointerEvents: draggedJob ? 'none' : 'auto'
                        }}
                        title="Click to view job details, drag to move"
                      >
                        <div className="font-bold text-[11px]">{statusConfig.icon} {statusConfig.label}</div>
                        <div className="font-bold text-[11px]">#{scheduledItem.job_id}</div>
                        <div className="font-medium text-[10px] truncate">{scheduledItem.customer_name || 'Customer'}</div>
                        <div className="text-[9px] truncate opacity-90">{scheduledItem.site_name || 'Site'}</div>
                        <div className="text-[9px] opacity-75">{formatClock(scheduledItem.start_hour, scheduledItem.start_minute)} ({duration}h)</div>
                      </div>
                    );
                  })}
                </div>
              </div>
              );
            })}
            </div>
          )}

          {/* Week View */}
          {view === 'week' && (
            <div className="pb-4">
              {/* Week Header Row */}
              <div className="flex sticky top-0 border-b border-gray-500 z-10 bg-gray-400" style={{ height: '44px' }}>
                {sidebarCollapsed && (
                  <div className="w-10 flex-shrink-0 px-1 bg-gray-400 border-r border-gray-500 flex items-center justify-center">
                    <button
                      onClick={() => setSidebarCollapsed(false)}
                      className="text-black hover:text-gray-700 text-sm"
                    >
                      ▶
                    </button>
                  </div>
                )}
                <div className="flex-shrink-0 px-2 text-black bg-gray-400 border-r border-gray-500 flex items-center justify-center" style={{ width: '200px' }}>
                  <select
                    value={filterPerson}
                    onChange={(e) => setFilterPerson(e.target.value)}
                    className="w-full px-2 py-1 bg-gray-300 border border-gray-400 rounded text-black text-sm font-semibold text-center focus:outline-none focus:ring-1 focus:ring-gray-500"
                  >
                    <option value="all" className="bg-gray-300 text-black">All Users</option>
                    <option value="engineers" className="bg-gray-300 text-black font-semibold">All Engineers</option>
                    <option value="office" className="bg-gray-300 text-black font-semibold">All Office Staff</option>
                    <option disabled className="bg-gray-400 text-black">────────────</option>
                    <optgroup label="Engineers" className="bg-gray-300 text-black">
                      {people.filter(p => {
                        const role = (p.role || p.job_title || '').toLowerCase();
                        return role.includes('engineer') || role.includes('technician');
                      }).map(p => (
                        <option key={p.id} value={p.id} className="bg-gray-300 text-black">
                          {p.first_name} {p.last_name}
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="Office Staff" className="bg-gray-300 text-black">
                      {people.filter(p => {
                        const role = (p.role || p.job_title || '').toLowerCase();
                        return !role.includes('engineer') && !role.includes('technician');
                      }).map(p => (
                        <option key={p.id} value={p.id} className="bg-gray-300 text-black">
                          {p.first_name} {p.last_name}
                        </option>
                      ))}
                    </optgroup>
                  </select>
                </div>
                {weekDates.map((date, i) => (
                  <div
                    key={i}
                    className="flex-1 px-2 text-center text-xs font-bold border-r border-gray-500 bg-gray-400 text-black flex items-center justify-center flex-col"
                  >
                    <div>{['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][i]}</div>
                    <div className="text-[10px] font-normal">{date.getDate()}/{date.getMonth() + 1}</div>
                  </div>
                ))}
              </div>

              {/* Person Rows */}
              {filteredPeople.map((person, index) => (
                <div key={person.id} className="flex border-b border-gray-200 hover:bg-gray-50">
                  {sidebarCollapsed && (
                    <div className={`w-10 flex-shrink-0 border-r border-gray-300 ${index % 2 === 1 ? 'bg-yellow-50' : 'bg-white'}`}></div>
                  )}
                  <div className={`flex-shrink-0 p-3 font-medium text-gray-900 border-r border-gray-300 flex items-center justify-center ${index % 2 === 1 ? 'bg-yellow-50' : 'bg-white'}`} style={{ width: '200px' }}>
                    <div className="text-center">
                      <div className="text-sm">{person.first_name} {person.last_name}</div>
                      <div className="text-xs text-gray-500">{person.role || 'Engineer'}</div>
                    </div>
                  </div>
                  
                  {weekDates.map((date, i) => {
                    const dateStr = dateKey(date);
                    const dayJobs = scheduleItems.filter(
                      item => Number(item.person_id) === Number(person.id) && 
                              item.schedule_date?.split('T')[0] === dateStr
                    );
                    return (
                      <div
                        key={`${person.id}-${dateStr}`}
                        data-testid={`dispatch-week-${person.id}-${dateStr}`}
                        onDragOver={(e) => { e.preventDefault(); if (e.dataTransfer) e.dataTransfer.dropEffect = 'move'; }}
                        onDrop={(e) => handleDropOnDate(e, person.id, date)}
                        className={`flex-1 min-h-[100px] max-h-[150px] border-r border-gray-200 p-2 overflow-y-auto ${
                          index % 2 === 1 ? 'bg-yellow-50' : 'bg-white'
                        }`}
                      >
                        {dayJobs.length === 0 ? (
                          <div className="text-center text-gray-400 text-xs italic py-4">No jobs</div>
                        ) : (
                          <div className="space-y-1">
                            {dayJobs.map(job => {
                              const statusConfig = jobStatuses.appearance(job.job_status, statusColors);
                              return (
                                <div
                                  key={job.id}
                                  data-status={statusConfig.status}
                                  onClick={(e) => handleJobClick(e, job.job_id)}
                                  className={`text-xs p-2 rounded border cursor-pointer transition ${statusConfig.blockClass}`}
                                >
                                  <div className="font-bold text-[11px]">{statusConfig.icon} {statusConfig.label}</div>
                                  <div className="font-bold text-[11px]">#{job.job_id}</div>
                                  <div className="font-medium text-[10px] truncate">{job.customer_name || 'Customer'}</div>
                                  <div className="text-[9px] truncate opacity-90">{job.site_name || 'Site'}</div>
                                  {job.start_hour !== undefined && (
                                    <div className="text-[9px] mt-1 opacity-75">
                                      ⏰ {formatClock(job.start_hour, job.start_minute)} ({job.duration_hours || 1}h)
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}

          {/* Month View */}
          {view === 'month' && (
            <div className="pb-4">
              {/* Month Header Row */}
              <div className="flex sticky top-0 border-b border-gray-500 z-10 bg-gray-400" style={{ height: '44px' }}>
                {sidebarCollapsed && (
                  <div className="w-10 flex-shrink-0 px-1 bg-gray-400 border-r border-gray-500 flex items-center justify-center">
                    <button
                      onClick={() => setSidebarCollapsed(false)}
                      className="text-black hover:text-gray-700 text-sm"
                    >
                      ▶
                    </button>
                  </div>
                )}
                <div className="flex-shrink-0 px-2 text-black bg-gray-400 border-r border-gray-500 flex items-center justify-center" style={{ width: '200px' }}>
                  <select
                    value={filterPerson}
                    onChange={(e) => setFilterPerson(e.target.value)}
                    className="w-full px-2 py-1 bg-gray-300 border border-gray-400 rounded text-black text-sm font-semibold text-center focus:outline-none focus:ring-1 focus:ring-gray-500"
                  >
                    <option value="all" className="bg-gray-300 text-black">All Users</option>
                    <option value="engineers" className="bg-gray-300 text-black font-semibold">All Engineers</option>
                    <option value="office" className="bg-gray-300 text-black font-semibold">All Office Staff</option>
                    <option disabled className="bg-gray-400 text-black">────────────</option>
                    <optgroup label="Engineers" className="bg-gray-300 text-black">
                      {people.filter(p => {
                        const role = (p.role || p.job_title || '').toLowerCase();
                        return role.includes('engineer') || role.includes('technician');
                      }).map(p => (
                        <option key={p.id} value={p.id} className="bg-gray-300 text-black">
                          {p.first_name} {p.last_name}
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="Office Staff" className="bg-gray-300 text-black">
                      {people.filter(p => {
                        const role = (p.role || p.job_title || '').toLowerCase();
                        return !role.includes('engineer') && !role.includes('technician');
                      }).map(p => (
                        <option key={p.id} value={p.id} className="bg-gray-300 text-black">
                          {p.first_name} {p.last_name}
                        </option>
                      ))}
                    </optgroup>
                  </select>
                </div>
                <div data-testid="dispatch-month-label" className="flex-1 px-2 text-center text-sm font-bold bg-gray-400 text-black flex items-center justify-center">
                  {currentDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
                </div>
              </div>

              {/* Day Headers */}
              <div className="flex border-b border-gray-500 bg-gray-300">
                {sidebarCollapsed && (
                  <div className="w-10 flex-shrink-0 border-r border-gray-500 bg-gray-300"></div>
                )}
                <div className="flex-shrink-0 border-r border-gray-500 bg-gray-300" style={{ width: '200px' }}></div>
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                  <div key={day} className="flex-1 p-2 text-center font-bold text-xs text-black border-r border-gray-500">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Grid */}
              {Array.from({ length: Math.ceil(monthDates.length / 7) }).map((_, weekIndex) => (
                <div key={weekIndex} className="flex">
                  {sidebarCollapsed && (
                    <div className={`w-10 flex-shrink-0 border-r border-gray-300 ${weekIndex % 2 === 1 ? 'bg-yellow-50' : 'bg-white'}`}></div>
                  )}
                  <div className={`flex-shrink-0 border-r border-gray-300 ${weekIndex % 2 === 1 ? 'bg-yellow-50' : 'bg-white'}`} style={{ width: '200px' }}></div>
                  <div className="flex flex-1">
                    {monthDates.slice(weekIndex * 7, (weekIndex + 1) * 7).map((date, dayIndex) => {
                      const dateStr = dateKey(date);
                      const dayJobs = scheduleItems.filter(item => {
                        const matchesDate = item.schedule_date?.split('T')[0] === dateStr;
                        if (filterPerson === 'all') return matchesDate;
                        if (filterPerson === 'engineers') {
                          const person = people.find(p => p.id === item.person_id);
                          const role = (person?.role || person?.job_title || '').toLowerCase();
                          return matchesDate && (role.includes('engineer') || role.includes('technician'));
                        }
                        if (filterPerson === 'office') {
                          const person = people.find(p => p.id === item.person_id);
                          const role = (person?.role || person?.job_title || '').toLowerCase();
                          return matchesDate && !role.includes('engineer') && !role.includes('technician');
                        }
                        return matchesDate && item.person_id === parseInt(filterPerson);
                      });
                      const isCurrentMonth = date.getMonth() === currentDate.getMonth();
                      const isToday = date.toDateString() === new Date().toDateString();
                      
                      return (
                        <div
                          key={dayIndex}
                          className={`flex-1 p-2 min-h-[100px] border-r border-b border-gray-200 ${
                            weekIndex % 2 === 1 ? 'bg-yellow-50' : 'bg-white'
                          } ${!isCurrentMonth ? 'opacity-40' : ''} ${isToday ? 'ring-2 ring-blue-500 ring-inset' : ''}`}
                        >
                          <div className={`text-sm font-bold mb-1 ${isToday ? 'text-blue-600' : 'text-gray-700'}`}>
                            {date.getDate()}
                          </div>
                          <div className="space-y-1">
                            {dayJobs.slice(0, 3).map((job, idx) => {
                              const statusConfig = jobStatuses.appearance(job.job_status, statusColors);
                              return (
                                <div
                                  key={idx}
                                  data-status={statusConfig.status}
                                  onClick={(e) => handleJobClick(e, job.job_id)}
                                  className={`text-[9px] p-1 rounded border cursor-pointer transition ${statusConfig.blockClass}`}
                                >
                                  <div className="font-bold">{statusConfig.icon} {statusConfig.label} #{job.job_id}</div>
                                  <div className="truncate">{job.customer_name || 'Customer'}</div>
                                </div>
                              );
                            })}
                            {dayJobs.length > 3 && (
                              <div className="text-[9px] text-gray-600 font-semibold">+{dayJobs.length - 3} more</div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Job Detail Modal */}
      {selectedJobId && jobDetail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={closeJobModal}>
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-3 rounded-t-lg flex items-center justify-between sticky top-0">
              <h2 className="text-lg font-bold">Job Details - {jobDetail.job_number}</h2>
              <button
                onClick={closeJobModal}
                className="text-white hover:bg-white/20 rounded-full p-1 transition text-xl leading-none"
              >
                ✕
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-4 space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-gray-600">Customer</label>
                  <p className="text-sm text-gray-900">{jobDetail.company_name || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600">Site</label>
                  <p className="text-sm text-gray-900">{jobDetail.site_name || jobDetail.site_address || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600">Job Category</label>
                  <p className="text-sm text-gray-900">{jobDetail.job_category || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600">Status</label>
                  <p className="text-sm text-gray-900">{jobDetail.job_status || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600">Priority</label>
                  <p className="text-sm text-gray-900">{jobDetail.call_priority || 'Standard'}</p>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600">PO Number</label>
                  <p className="text-sm text-gray-900">{jobDetail.po_number || 'N/A'}</p>
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-600">Description</label>
                <p className="text-sm text-gray-900">{jobDetail.job_description || 'No description'}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-gray-600">Quoted Price</label>
                  <p className="text-sm text-gray-900">£{jobDetail.quoted_price || '0.00'}</p>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600">Response Time</label>
                  <p className="text-sm text-gray-900">{jobDetail.response_time || 'N/A'}</p>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 px-4 py-3 rounded-b-lg flex justify-between items-center gap-2">
              <div className="flex gap-2">
                <button
                  onClick={async () => {
                    closeJobModal();
                    // Fetch full job details for editing
                    try {
                      const res = await apiFetch(`/api/jobs/${selectedJobId}`);
                      const data = await res.json();
                      setEditingJob(data);
                      setEditStatusOrigin(data.job_status || '');
                      setEditStatusReason('');
                      setEditingJobId(selectedJobId);
                    } catch (err) {
                      console.error('Error fetching job for edit:', err);
                    }
                  }}
                  className="px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition"
                >
                  Edit Job
                </button>
                <button
                  onClick={() => {
                    closeJobModal();
                    navigate(`/jobs/${selectedJobId}`);
                  }}
                  className="px-3 py-2 bg-gray-500 text-white text-sm rounded-lg hover:bg-gray-600 transition"
                >
                  Full Details
                </button>
              </div>
              <button
                onClick={closeJobModal}
                className="px-3 py-2 bg-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-400 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Large Edit Job Modal */}
      {editingJobId && editingJob && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-8" onClick={() => setEditingJobId(null)}>
          <div className="bg-white rounded-lg shadow-2xl w-[80%] h-[80%] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex items-center justify-between flex-shrink-0">
              <h2 className="text-xl font-bold text-white">Edit Job - {editingJob.job_number}</h2>
              <button
                onClick={() => setEditingJobId(null)}
                className="text-white hover:bg-white/20 rounded-full p-2 transition text-xl leading-none"
              >
                ✕
              </button>
            </div>

            {/* Scrollable form body */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Customer</label>
                  <input
                    type="text"
                    value={editingJob.company_name || ''}
                    onChange={(e) => setEditingJob({...editingJob, company_name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Site</label>
                  <input
                    type="text"
                    value={editingJob.site_name || ''}
                    onChange={(e) => setEditingJob({...editingJob, site_name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Job Category</label>
                  <input
                    type="text"
                    value={editingJob.job_category || ''}
                    onChange={(e) => setEditingJob({...editingJob, job_category: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Job Status</label>
                  <select
                    aria-label="Job status"
                    value={jobStatuses.normalizeJobStatus(editingJob.job_status) || jobStatuses.DEFAULT_JOB_STATUS}
                    onChange={(e) => setEditingJob({...editingJob, job_status: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  >
                    {jobStatuses.JOB_STATUSES.map((status) => (
                      <option key={status.value} value={status.value}>{status.label}</option>
                    ))}
                  </select>
                  {(jobStatuses.normalizeJobStatus(editingJob.job_status) || jobStatuses.DEFAULT_JOB_STATUS)
                    !== (jobStatuses.normalizeJobStatus(editStatusOrigin) || jobStatuses.DEFAULT_JOB_STATUS) && (
                    <textarea
                      data-testid="dispatch-status-reason"
                      aria-label="Reason for the status change"
                      value={editStatusReason}
                      onChange={(e) => setEditStatusReason(e.target.value)}
                      rows={2}
                      placeholder="Reason for changing the status"
                      className="w-full mt-2 px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                    />
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Priority</label>
                  <select
                    value={editingJob.call_priority || 'Standard'}
                    onChange={(e) => setEditingJob({...editingJob, call_priority: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="Emergency">Emergency</option>
                    <option value="Urgent">Urgent</option>
                    <option value="Standard">Standard</option>
                    <option value="Low">Low</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Estimated Hours</label>
                  <input
                    type="number"
                    value={editingJob.estimated_hours || 1}
                    onChange={(e) => setEditingJob({...editingJob, estimated_hours: parseFloat(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Description</label>
                  <textarea
                    value={editingJob.job_description || ''}
                    onChange={(e) => setEditingJob({...editingJob, job_description: e.target.value})}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Quoted Price</label>
                  <input
                    type="number"
                    value={editingJob.quoted_price || ''}
                    onChange={(e) => setEditingJob({...editingJob, quoted_price: parseFloat(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">PO Number</label>
                  <input
                    type="text"
                    value={editingJob.po_number || ''}
                    onChange={(e) => setEditingJob({...editingJob, po_number: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-6 py-4 border-t flex justify-end gap-3 flex-shrink-0">
              <button
                onClick={() => setEditingJobId(null)}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  const origin = jobStatuses.normalizeJobStatus(editStatusOrigin) || jobStatuses.DEFAULT_JOB_STATUS;
                  const next = jobStatuses.normalizeJobStatus(editingJob.job_status) || jobStatuses.DEFAULT_JOB_STATUS;
                  const body = { ...editingJob };
                  if (next !== origin) {
                    if (!editStatusReason.trim()) {
                      alert('A reason is required to change the status by hand.');
                      return;
                    }
                    body.status_reason = editStatusReason.trim();
                  }
                  try {
                    const res = await apiFetch(`/api/jobs/${editingJobId}`, {
                      method: 'PUT',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify(body)
                    });
                    const saved = await res.json().catch(() => ({}));
                    if (!res.ok) {
                      alert(saved.error || 'Failed to save job');
                      return;
                    }
                    setEditingJobId(null);
                    setEditingJob(null);
                    setEditStatusReason('');
                    fetchSchedule();
                    fetchUnassignedJobs();
                  } catch (err) {
                    console.error('Error saving job:', err);
                    alert('Failed to save job');
                  }
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EngineerDispatch;
